import { createElement as rc, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dbViews as dbViewServices, globalConfig } from 'lib_ui-services';
import useEventSink from '../../hooks/useEventSink';

const SEARCHING_TIMEOUT_MS = 6000;
const SEARCHING_ANIMATION_INTERVAL_MS = 500;

const { minFullTextSearchTermLength } = globalConfig();
const { LOCAL_ONLY_SEARCH_REASONS } = dbViewServices;

// The always-visible status line under the grid, for when the rows on screen are only the part of
// the answer that happens to be on this device. The grid's last row says the same thing, but the
// user has to reach the bottom of the list to read it, and the search box only mentions the term
// length minimum in placeholder text that disappears as soon as they type (#2040).
const LOCAL_ONLY_SEARCHING_TEXT = {
    [LOCAL_ONLY_SEARCH_REASONS.termTooShort]:
        `Searching this device only. Type ${minFullTextSearchTermLength} or more characters to search all records.`,
    [LOCAL_ONLY_SEARCH_REASONS.serverUnavailable]:
        'The server search is unavailable, so only records on this device were searched.',
    [LOCAL_ONLY_SEARCH_REASONS.noServerSearch]:
        'Searching this device only. This list is not searched on the server, so there may be more.'
};

/**
 * Shared business logic for GL2025 Grid component (web and native)
 * Handles column separation, scroll synchronization, and row detail wrapping
 */
export function useGridLogic(props, theme) {
    const {
        columnHNodes,
        columnWidths,
        RowDetail,
        onScrollLayoutChange,
        hNode
    } = props;

    const pinnedListRef = useRef(null);
    const scrollableListRef = useRef(null);
    const scrollableContainerRef = useRef(null);

    // Determine which columns are pinned
    const includeCheckbox = hNode?.includeCheckbox;

    // Set to true to use hardcoded 2 pinned columns for demo purposes
    const useDebugMode = false;

    // Separate columns into pinned and scrollable based on mode
    let pinnedColumns, scrollableColumns, pinnedColumnIndices, scrollableColumnIndices;
    if (useDebugMode) {
        // DEBUG: Hardcoded 2 columns for demonstration
        pinnedColumns = columnHNodes.slice(0, 2);
        scrollableColumns = columnHNodes.slice(2);
        pinnedColumnIndices = [0, 1];
        scrollableColumnIndices = Array.from({ length: scrollableColumns.length }, (_, i) => i + 2);
    } else {
        // PRODUCTION: Separate columns based on pinOnDesktop or pinOnMobile property
        const isMobile = theme?.mobile || false;
        const pinProperty = isMobile ? 'pinOnMobile' : 'pinOnDesktop';

        // Track original indices for width lookup and column rendering
        pinnedColumns = [];
        scrollableColumns = [];
        pinnedColumnIndices = [];
        scrollableColumnIndices = [];

        columnHNodes.forEach((col, idx) => {
            if (col[pinProperty] === true) {
                pinnedColumns.push(col);
                pinnedColumnIndices.push(idx);
            } else {
                scrollableColumns.push(col);
                scrollableColumnIndices.push(idx);
            }
        });

        // If no columns are pinned (pinnedColumns.length === 0), will render single-grid layout
    }

    let pinnedCount = pinnedColumns.length;
    if (pinnedCount > 0 && scrollableColumns.length > 0 && scrollableColumns[0].hNodeType === 'button') {
        // move button columns to the head of the pinned columns
        const actionButtonColumn = scrollableColumns.shift();
        const actionButtonColumnIndex = scrollableColumnIndices.shift();
        pinnedColumns.unshift(actionButtonColumn);
        pinnedColumnIndices.unshift(actionButtonColumnIndex);
        // reset pinned column count
        pinnedCount = pinnedColumns.length;
    }

    // Calculate pinned width (checkbox + pinned column widths)
    const checkboxWidth = includeCheckbox ? 48 : 0;
    const pinnedDataWidth = pinnedColumnIndices.reduce((sum, originalIdx) =>
        sum + (columnWidths[originalIdx] || 150), 0
    );
    const pinnedWidth = checkboxWidth + pinnedDataWidth;
    const scrollableWidth = scrollableColumnIndices.reduce((sum, originalIdx) =>
        sum + (columnWidths[originalIdx] || 150), 0
    );

    // Track which grid is currently being programmatically scrolled to prevent loops
    const isSyncingRef = useRef(false);

    // Store layout refs to access scrollTo from other grid
    const pinnedLayoutRef = useRef(null);
    const scrollableLayoutRef = useRef(null);

    // Track the ScrollableContainer's actual viewport width.
    // On native, the FlatList inside a horizontal ScrollView expands to content width
    // (e.g. 11,000px for 20 columns), so its onLayout/onScroll reports that as clientWidth.
    // Column virtualization then thinks ALL columns are visible and renders them all.
    // This ref stores the container's real visible width so we can override clientWidth.
    const scrollableViewportWidthRef = useRef(0);

    const handleScrollableContainerLayout = useCallback((event) => {
        scrollableViewportWidthRef.current = event.nativeEvent.layout.width;
    }, []);

    // Handle horizontal scroll on the ScrollableContainer (RNScrollView).
    // The FlatList only reports vertical scroll. Horizontal scroll events come from
    // the container and must update scrollLeft for column virtualization.
    const scrollableScrollLeftRef = useRef(0);
    const handleScrollableContainerScroll = useCallback((event) => {
        const scrollLeft = event.nativeEvent.contentOffset.x;
        scrollableScrollLeftRef.current = scrollLeft;
        // Build a layout object with horizontal info and forward to column virtualization
        const viewportWidth = scrollableViewportWidthRef.current || event.nativeEvent.layoutMeasurement.width;
        onScrollLayoutChange({
            scrollTop: scrollableLayoutRef.current?.scrollTop || 0,
            scrollLeft,
            clientHeight: scrollableLayoutRef.current?.clientHeight || 0,
            clientWidth: viewportWidth,
            scrollHeight: scrollableLayoutRef.current?.scrollHeight || 0,
            scrollWidth: event.nativeEvent.contentSize.width,
            scrollTo: scrollableLayoutRef.current?.scrollTo
        });
    }, [onScrollLayoutChange]);

    // Sync vertical scroll between both grids.
    // isSyncingRef prevents scroll sync loops: scrollToOffset is async, so the flag
    // must stay true until the next frame to catch the resulting onScroll event.
    const handlePinnedScroll = useCallback((layout) => {
        pinnedLayoutRef.current = layout;
        if (isSyncingRef.current) return;
        if (scrollableLayoutRef.current?.scrollTo) {
            isSyncingRef.current = true;
            scrollableLayoutRef.current.scrollTo({ top: layout.scrollTop });
            requestAnimationFrame(() => { isSyncingRef.current = false; });
        }
    }, []);

    const handleScrollableScroll = useCallback((layout) => {
        scrollableLayoutRef.current = layout;
        // Override clientWidth with the ScrollableContainer's actual viewport width
        // if available. The FlatList reports its own width (full content, e.g. 11K)
        // instead of the visible viewport width, which breaks column virtualization.
        // Also override scrollLeft — the FlatList always reports 0 because it doesn't
        // scroll horizontally (the outer ScrollableContainer does).
        const correctedLayout = scrollableViewportWidthRef.current
            ? { ...layout, clientWidth: scrollableViewportWidthRef.current, scrollLeft: scrollableScrollLeftRef.current }
            : layout;
        onScrollLayoutChange(correctedLayout);
        if (isSyncingRef.current) return;
        if (pinnedLayoutRef.current?.scrollTo) {
            isSyncingRef.current = true;
            pinnedLayoutRef.current.scrollTo({ top: layout.scrollTop });
            requestAnimationFrame(() => { isSyncingRef.current = false; });
        }
    }, [onScrollLayoutChange]);

    // Single-grid scroll handler — same clientWidth/scrollLeft correction as above
    // but without two-grid vertical sync logic.
    const handleSingleGridScroll = useCallback((layout) => {
        scrollableLayoutRef.current = layout;
        const correctedLayout = scrollableViewportWidthRef.current
            ? { ...layout, clientWidth: scrollableViewportWidthRef.current, scrollLeft: scrollableScrollLeftRef.current }
            : layout;
        onScrollLayoutChange(correctedLayout);
    }, [onScrollLayoutChange]);

    // Create pinned-specific RowDetail wrapper component
    // Memoize to ensure stable reference and proper closure over RowDetail
    const PinnedRowDetail = useMemo(() => {
        // Return a component function that will be called by RenderDefaultRow
        return function PinnedRowDetailComponent(props) {
            return rc(RowDetail, {
                ...props,
                isPinned: true,
                visibleColumnIndexes: pinnedColumnIndices
            });
        };
    }, [RowDetail, pinnedColumnIndices]);

    // Create scrollable-specific RowDetail wrapper component
    // Memoize to ensure stable reference and proper closure over RowDetail
    const ScrollableRowDetail = useMemo(() => {
        // Map scrollable column widths using their original indices
        const scrollableWidths = scrollableColumnIndices.map(idx => columnWidths[idx]);

        // Return a component function that will be called by RenderDefaultRow
        return function ScrollableRowDetailComponent(props) {
            return rc(RowDetail, {
                ...props,
                isPinned: false,
                // Pass scrollable columns subset - virtualization will calculate visible indices
                columnHNodesOverride: scrollableColumns,
                columnWidthsOverride: scrollableWidths
                // Don't pass visibleColumnIndexes - let component calculate based on scroll
            });
        };
    }, [RowDetail, scrollableColumns, scrollableColumnIndices, columnWidths]);

    // Create single-grid RowDetail wrapper for when no columns are pinned
    // Memoize to ensure stable reference and proper closure over RowDetail
    const SingleGridRowDetail = useMemo(() => {
        return function SingleGridRowDetailComponent(props) {
            // Don't pass visibleColumnIndexes - let component calculate based on scroll for virtualization
            return rc(RowDetail, {
                ...props
            });
        };
    }, [RowDetail]);

    return {
        // Refs
        pinnedListRef,
        scrollableListRef,
        scrollableContainerRef,

        // Column data
        pinnedColumns,
        scrollableColumns,
        pinnedColumnIndices,
        scrollableColumnIndices,
        pinnedCount,
        pinnedWidth,
        scrollableWidth,

        // Handlers
        handlePinnedScroll,
        handleScrollableScroll,
        handleSingleGridScroll,
        handleScrollableContainerLayout,
        handleScrollableContainerScroll,

        // Row detail wrappers
        PinnedRowDetail,
        ScrollableRowDetail,
        SingleGridRowDetail
    };
}

/**
 * Shared rendering function for pinned grid in two-grid layout
 */
export function renderPinnedGrid(gridData, platformComponents) {
    const {
        id,
        records,
        recordCount,
        onClick,
        onRenderItem,
        listType,
        endOfRecordsText,
        offlineText,
        hNode,
        headerClicked,
        sortedColumns,
        columnWidths,
        handleColumnWidthChange,
        pinnedListRef,
        pinnedColumns,
        pinnedCount,
        pinnedWidth,
        handlePinnedScroll,
        PinnedRowDetail,
        hasHorizontalScrollbar,
        scrollEnabled
    } = gridData;

    const { Row, PinnedContainer, List, HeaderRow } = platformComponents;

    return rc(PinnedContainer, {
        style: {
            width: pinnedWidth,
            // Web needs padding for horizontal scrollbar sync, native doesn't
            paddingBottom: hasHorizontalScrollbar ? 8 : 0
        }
    },
        rc(List, {
            ref: pinnedListRef,
            id: `${id}-pinned`,
            data: records,
            itemCount: recordCount,
            onScrollLayoutChange: handlePinnedScroll,
            onClick,
            onRenderItem,
            Row,
            RowDetail: PinnedRowDetail,
            listType,
            endOfRecordsText,
            offlineText,
            hideVerticalScrollOverlay: true,
            scrollEnabled
        },
            rc(HeaderRow, {
                hNode,
                headerClicked,
                sortedColumns,
                columnHNodes: pinnedColumns,
                columnWidths: columnWidths.slice(0, pinnedCount),
                handleColumnWidthChange,
                isPinned: true,
                borderRadiusTopLeft: true,
                borderRadiusTopRight: false
            })
        )
    );
}

/**
 * Shared rendering function for scrollable grid in two-grid layout
 */
export function renderScrollableGrid(gridData, platformComponents) {
    const {
        id,
        records,
        recordCount,
        onClick,
        onRenderItem,
        listType,
        endOfRecordsText,
        offlineText,
        hNode,
        headerClicked,
        sortedColumns,
        columnWidths,
        handleColumnWidthChange,
        scrollableListRef,
        scrollableContainerRef,
        scrollableColumns,
        pinnedCount,
        handleScrollableScroll,
        ScrollableRowDetail,
        scrollableContainerProps,
        scrollEnabled
    } = gridData;

    const { Row, ScrollableContainer, List, HeaderRow } = platformComponents;

    return rc(ScrollableContainer, { ref: scrollableContainerRef, ...scrollableContainerProps },
        rc(List, {
            ref: scrollableListRef,
            id: `${id}-scrollable`,
            data: records,
            itemCount: recordCount,
            onScrollLayoutChange: handleScrollableScroll,
            onClick,
            onRenderItem,
            Row,
            RowDetail: ScrollableRowDetail,
            listType,
            endOfRecordsText,
            offlineText,
            scrollEnabled
        },
            rc(HeaderRow, {
                hNode,
                headerClicked,
                sortedColumns,
                columnHNodes: scrollableColumns,
                columnWidths: columnWidths.slice(pinnedCount),
                handleColumnWidthChange,
                isPinned: false,
                borderRadiusTopLeft: false,
                borderRadiusTopRight: true
            })
        )
    );
}

/**
 * Shared rendering function for single-grid layout (when no columns are pinned)
 * Platform-specific files pass their Row component
 */
export function renderSingleGrid(gridData, platformComponents) {
    const {
        id,
        records,
        recordCount,
        onScrollLayoutChange,
        onClick,
        onRenderItem,
        listType,
        endOfRecordsText,
        offlineText,
        hNode,
        headerClicked,
        sortedColumns,
        columnHNodes,
        columnWidths,
        handleColumnWidthChange,
        scrollableContainerRef,
        scrollableListRef,
        SingleGridRowDetail,
        scrollableContainerProps,
        scrollEnabled
    } = gridData;

    const { Row, ScrollableContainer, List, HeaderRow } = platformComponents;

    return rc(ScrollableContainer, { 'data-grid-id': id, ref: scrollableContainerRef, ...scrollableContainerProps },
        rc(List, {
            ref: scrollableListRef,
            id: `${id}-scrollable`,
            data: records,
            itemCount: recordCount,
            onScrollLayoutChange,
            onClick,
            onRenderItem,
            Row,
            RowDetail: SingleGridRowDetail,
            listType,
            endOfRecordsText,
            offlineText,
            scrollEnabled
        },
            rc(HeaderRow, {
                hNode,
                headerClicked,
                sortedColumns,
                columnHNodes,
                columnWidths,
                handleColumnWidthChange,
                borderRadiusTopLeft: true,
                borderRadiusTopRight: true
            })
        )
    );
}

/**
 * The text for the grid's status line.
 *
 * @param {number} recordCount
 * @param {boolean} awaitingFirstServerPage - a #332 stage-2 first page is in flight
 * @param {string} [localOnlySearchReason] - set when the current search was answered without the
 * server; see `getLocalOnlySearchReason`
 */
export function useSearchingText(recordCount, awaitingFirstServerPage, localOnlySearchReason) {
    const [subscribe] = useEventSink(true) ?? [];
    const [searchingText, setSearchingText] = useState('');
    // Track the last time dispatchRead fired so we only show Searching... after
    // the user actually submitted a value, not on initial empty-list renders.
    const [lastDispatchTime, setLastDispatchTime] = useState(null);

    useEffect(() => {
        if (subscribe == null) return;
        return subscribe(
            // subscribe to a user scanning or typing a search term
            { verb: 'change', namespace: 'sensor', relation: 'read' },
            () => setLastDispatchTime(Date.now())
        );
    }, [subscribe]);

    useEffect(() => {
        if (awaitingFirstServerPage) {
            setSearchingText('');
            return;
        }
        if (recordCount === 0 && lastDispatchTime != null) {
            const intervalId = setInterval(() => {
                if (Date.now() - lastDispatchTime > SEARCHING_TIMEOUT_MS) {
                    setSearchingText('No records found');
                    clearInterval(intervalId);
                } else {
                    setSearchingText(prev => ['', 'Searching...', 'No records found'].includes(prev) ? 'Searching' : prev + '.');
                }
            }, SEARCHING_ANIMATION_INTERVAL_MS);
            return () => clearInterval(intervalId);
        } else {
            setSearchingText(''); // Clear searching text when records are found
        }
    }, [recordCount, lastDispatchTime, awaitingFirstServerPage]);
    // #332: while the Go Fetch stage-2 first-page fetch is in flight, the always-visible
    // searching slot shows the stage-1 message regardless of the scan-driven state. This is
    // a separate concern from the in-grid "End of Records" text (which only shows when empty).
    if (awaitingFirstServerPage) return 'Displaying initial results, searching...';
    // Outranks the scan-driven text below: with a local-only answer, "No records found" would be
    // a claim about the tenant that only this device's rows support.
    if (localOnlySearchReason && LOCAL_ONLY_SEARCHING_TEXT[localOnlySearchReason]) {
        return LOCAL_ONLY_SEARCHING_TEXT[localOnlySearchReason];
    }
    return searchingText;
}
